import type {
	ComponentPropsWithRef,
	HTMLAttributes,
	ReactNode,
	Ref,
	TdHTMLAttributes,
	ThHTMLAttributes,
} from "react";
import type {
	CellProps as AriaCellProps,
	ColumnProps as AriaColumnProps,
	RowProps as AriaRowProps,
	TableHeaderProps as AriaTableHeaderProps,
	TableProps as AriaTableProps,
} from "react-aria-components";

import {
	ArrowDown,
	ChevronSelectorVertical,
	Copy01,
	Edit01,
	HelpCircle,
	Trash01,
} from "@untitledui/icons";
import {
	createContext,
	isValidElement,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Cell as AriaCell,
	Collection as AriaCollection,
	Column as AriaColumn,
	Group as AriaGroup,
	Row as AriaRow,
	Table as AriaTable,
	TableBody as AriaTableBody,
	TableHeader as AriaTableHeader,
	useTableOptions,
} from "react-aria-components";

import { Badge } from "@/components/ui/badges/badges";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Dropdown } from "@/components/ui/dropdown/dropdown-index";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { mergeRefs } from "@/utils/merge-refs";

export const TableRowActionsDropdown = () => (
	<Dropdown.Root>
		<Dropdown.DotsButton />

		<Dropdown.Popover className="w-min">
			<Dropdown.Menu>
				<Dropdown.Item icon={Edit01}>
					<span className="pr-4">Edit</span>
				</Dropdown.Item>
				<Dropdown.Item icon={Copy01}>
					<span className="pr-4">Copy link</span>
				</Dropdown.Item>
				<Dropdown.Item icon={Trash01}>
					<span className="pr-4">Delete</span>
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown.Popover>
	</Dropdown.Root>
);

const TableContext = createContext<{ size: "md" | "sm" }>({ size: "md" });

const TableCardRoot = ({
	children,
	className,
	size = "md",
	...props
}: HTMLAttributes<HTMLDivElement> & { size?: "md" | "sm" }) => {
	return (
		<TableContext.Provider value={{ size }}>
			{/* shrink-0 keeps the card from being squeezed by parent flex layouts on
			    short viewports — content stays visible and scrollable on small devices. */}
			<div
				{...props}
				className={cx(
					"scrollbar-hide flex w-full shrink-0 flex-col overflow-hidden overflow-y-auto rounded-xl bg-primary shadow-xs ring-1 ring-secondary",
					className,
				)}
			>
				{children}
			</div>
		</TableContext.Provider>
	);
};

interface TableCardHeaderProps {
	/** The badge displayed next to the title. */
	badge?: ReactNode;
	/** The class name of the table card header. */
	className?: string;
	/** The content displayed after the title and badge. */
	contentTrailing?: ReactNode;
	/** The description of the table card header. */
	description?: string;
	/** The title of the table card header. */
	title: ReactNode | string;
}

const TableCardHeader = ({
	badge,
	className,
	contentTrailing,
	description,
	title,
}: TableCardHeaderProps) => {
	const { size } = useContext(TableContext);

	return (
		<div
			className={cx(
				"relative flex flex-col items-start gap-4 border-b border-secondary bg-primary px-4 md:flex-row",
				size === "sm" ? "py-4 md:px-5" : "py-5 md:px-6",
				className,
			)}
		>
			<div className="flex flex-1 flex-col gap-0.5">
				<div className="flex items-center gap-2">
					{typeof title === "string" ? (
						<h2
							className={cx(
								"font-semibold text-primary",
								size === "sm" ? "text-md" : "text-lg",
							)}
						>
							{title}
						</h2>
					) : (
						title
					)}
					{badge ? (
						isValidElement(badge) ? (
							badge
						) : (
							<Badge color="brand" size="sm">
								{badge}
							</Badge>
						)
					) : null}
				</div>
				{description && <p className="text-sm text-tertiary">{description}</p>}
			</div>
			{contentTrailing}
		</div>
	);
};

interface TableRootProps
	extends
		AriaTableProps,
		Omit<ComponentPropsWithRef<"table">, "className" | "slot" | "style"> {
	/**
	 * Optional ref on the horizontal scroll wrapper. List-restore attaches a
	 * capture-phase keydown listener here so Enter/Space on a row link records
	 * the anchor before navigation (without breaking table layout).
	 */
	scrollContainerRef?: Ref<HTMLDivElement>;
	/**
	 * `"x"` (default) wraps the table in a horizontal scroller. Use `"none"` when
	 * an ancestor owns scrolling — needed for sticky headers, since any nested
	 * overflow box becomes the sticky containing block and prevents sticking.
	 */
	scrollMode?: "none" | "x";
	size?: "md" | "sm";
}

const TableRoot = ({
	className,
	scrollContainerRef,
	scrollMode = "x",
	size = "md",
	...props
}: TableRootProps) => {
	const context = useContext(TableContext);
	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const ownsHorizontalScroll = scrollMode === "x";

	useEffect(() => {
		if (!ownsHorizontalScroll) return;

		const el = scrollRef.current;
		if (!el) return;

		const checkScroll = () => {
			setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
		};

		checkScroll();
		el.addEventListener("scroll", checkScroll, { passive: true });
		const observer = new ResizeObserver(checkScroll);
		observer.observe(el);

		return () => {
			el.removeEventListener("scroll", checkScroll);
			observer.disconnect();
		};
	}, [ownsHorizontalScroll]);

	// Memoized so the callback identity survives a re-render. An inline
	// mergeRefs() makes React detach the ref (calling it with null) and reattach
	// it every render, which briefly hides the scroll container from list
	// restore's keyboard listener mid-render.
	const handleScrollContainerRef = useMemo(
		() => mergeRefs([scrollRef, scrollContainerRef]),
		[scrollContainerRef],
	);

	const table = (
		<AriaTable
			className={(state) =>
				cx(
					"w-full",
					typeof className === "function" ? className(state) : className,
				)
			}
			{...props}
		/>
	);

	return (
		<TableContext.Provider value={{ size: context?.size ?? size }}>
			{ownsHorizontalScroll ? (
				<div
					className="scrollbar-horizontal-only overflow-x-auto"
					data-can-scroll-right={canScrollRight || undefined}
					ref={handleScrollContainerRef}
				>
					{table}
				</div>
			) : (
				table
			)}
		</TableContext.Provider>
	);
};
TableRoot.displayName = "Table";

interface TableHeaderProps<T extends object>
	extends
		AriaTableHeaderProps<T>,
		Omit<
			ComponentPropsWithRef<"thead">,
			"children" | "className" | "slot" | "style"
		> {
	bordered?: boolean;
	/**
	 * Stick this header to the top of the nearest scrollport while its table
	 * remains in view. Pass a Tailwind `top-*` class when stacking nested
	 * sticky headers (e.g. `top-9` under an `sm` parent header).
	 */
	sticky?: boolean | { className?: string };
}

const TableHeader = <T extends object>({
	bordered = true,
	children,
	className,
	columns,
	sticky = false,
	...props
}: TableHeaderProps<T>) => {
	const { size } = useContext(TableContext);
	const { selectionBehavior, selectionMode } = useTableOptions();
	const stickyClassName =
		sticky === true
			? "sticky top-0 z-10"
			: sticky
				? cx("sticky z-10", sticky.className ?? "top-0")
				: undefined;

	return (
		<AriaTableHeader
			{...props}
			className={(state) =>
				cx(
					"relative bg-secondary",
					size === "sm" ? "h-9" : "h-11",
					stickyClassName,
					// Opaque cells so scrolling rows don't show through the sticky bar.
					sticky && "[&>tr>th]:bg-secondary",

					// Row border—using an "after" pseudo-element to avoid the border taking up space.
					bordered &&
						"[&>tr>th]:after:pointer-events-none [&>tr>th]:after:absolute [&>tr>th]:after:inset-x-0 [&>tr>th]:after:bottom-0 [&>tr>th]:after:h-px [&>tr>th]:after:bg-border-secondary [&>tr>th]:focus-visible:after:bg-transparent",

					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{selectionBehavior === "toggle" && (
				<AriaColumn
					className={cx(
						"relative py-2 pr-0 pl-4",
						size === "sm" ? "w-9 md:pl-5" : "w-11 md:pl-6",
					)}
				>
					{selectionMode === "multiple" && (
						<div className="flex items-start">
							<Checkbox size={size} slot="selection" />
						</div>
					)}
				</AriaColumn>
			)}
			<AriaCollection items={columns}>{children}</AriaCollection>
		</AriaTableHeader>
	);
};

TableHeader.displayName = "TableHeader";

interface TableHeadProps
	extends
		AriaColumnProps,
		Omit<
			ThHTMLAttributes<HTMLTableCellElement>,
			"children" | "className" | "id" | "style"
		> {
	label?: string;
	tooltip?: string;
}

const TableHead = ({
	children,
	className,
	label,
	tooltip,
	...props
}: TableHeadProps) => {
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaColumn
			{...props}
			className={(state) =>
				cx(
					"relative p-0 px-6 py-2 outline-hidden focus-visible:z-1 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-bg-primary focus-visible:ring-inset",
					selectionBehavior === "toggle" && "nth-2:pl-3",
					state.allowsSorting && "cursor-pointer",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{(state) => (
				<AriaGroup className="flex items-center gap-1">
					<div className="flex items-center gap-1">
						{label && (
							<span className="text-xs font-semibold whitespace-nowrap text-quaternary">
								{label}
							</span>
						)}
						{typeof children === "function" ? children(state) : children}
					</div>

					{tooltip && (
						<Tooltip placement="top" title={tooltip}>
							<TooltipTrigger className="cursor-pointer text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
								<HelpCircle className="size-4" />
							</TooltipTrigger>
						</Tooltip>
					)}

					{state.allowsSorting &&
						(state.sortDirection ? (
							<ArrowDown
								className={cx(
									"size-3 stroke-[3px] text-fg-quaternary",
									state.sortDirection === "ascending" && "rotate-180",
								)}
							/>
						) : (
							<ChevronSelectorVertical
								className="text-fg-quaternary"
								size={12}
								strokeWidth={3}
							/>
						))}
				</AriaGroup>
			)}
		</AriaColumn>
	);
};
TableHead.displayName = "TableHead";

interface TableRowProps<T extends object>
	extends
		AriaRowProps<T>,
		Omit<
			ComponentPropsWithRef<"tr">,
			"children" | "className" | "id" | "onClick" | "slot" | "style"
		> {
	highlightSelectedRow?: boolean;
}

const TableRow = <T extends object>({
	children,
	className,
	columns,
	highlightSelectedRow = true,
	...props
}: TableRowProps<T>) => {
	const { size } = useContext(TableContext);
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaRow
			{...props}
			className={(state) =>
				cx(
					"relative outline-focus-ring transition-colors after:pointer-events-none hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2",
					size === "sm" ? "h-14" : "h-18",
					highlightSelectedRow && "selected:bg-secondary",

					// Row border—using an "after" pseudo-element to avoid the border taking up space.
					"[&>td]:after:absolute [&>td]:after:inset-x-0 [&>td]:after:bottom-0 [&>td]:after:h-px [&>td]:after:w-full [&>td]:after:bg-border-secondary last:[&>td]:after:hidden [&>td]:focus-visible:after:opacity-0 focus-visible:[&>td]:after:opacity-0",

					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{selectionBehavior === "toggle" && (
				<AriaCell
					className={cx(
						"relative py-2 pr-0 pl-4",
						size === "sm" ? "md:pl-5" : "md:pl-6",
					)}
				>
					<div className="flex items-end">
						<Checkbox size={size} slot="selection" />
					</div>
				</AriaCell>
			)}
			<AriaCollection items={columns}>{children}</AriaCollection>
		</AriaRow>
	);
};

TableRow.displayName = "TableRow";

interface TableCellProps
	extends
		AriaCellProps,
		Omit<
			TdHTMLAttributes<HTMLTableCellElement>,
			"children" | "className" | "id" | "style"
		> {
	ref?: Ref<HTMLTableCellElement>;
}

const TableCell = ({ children, className, ...props }: TableCellProps) => {
	const { size } = useContext(TableContext);
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaCell
			{...props}
			className={(state) =>
				cx(
					"relative text-sm text-tertiary outline-focus-ring focus-visible:z-1 focus-visible:outline-2 focus-visible:-outline-offset-2",
					size === "sm" && "px-5 py-3",
					size === "md" && "px-6 py-4",

					selectionBehavior === "toggle" && "nth-2:pl-3",

					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{children}
		</AriaCell>
	);
};
TableCell.displayName = "TableCell";

const TableCard = {
	Header: TableCardHeader,
	Root: TableCardRoot,
};

const Table = TableRoot as typeof TableRoot & {
	Body: typeof AriaTableBody;
	Cell: typeof TableCell;
	Head: typeof TableHead;
	Header: typeof TableHeader;
	Row: typeof TableRow;
};
Table.Body = AriaTableBody;
Table.Cell = TableCell;
Table.Head = TableHead;
Table.Header = TableHeader;
Table.Row = TableRow;

export { Table, TableCard };
